/**
 * Zentrales Modul zur Definition aller 247 Zustände mit ihrem AlarmLevel.
 */

/**
 * --- 1. Definition der AlarmLevel Konstanten (Wird von allen Zuständen benötigt) ---
 */
export const AlarmLevel = Object.freeze({
  GRUEN: '#73b025',
  GELB: '#eda512',
  ROT: '#e3150f'
})

/**
 * --- 2. Definition des zusammengefassten Enum-Objekts (Unveränderlich) ---
 */
export const DeviceStates = Object.freeze({
  // --- Zustände aus Trap State ---
  TRIGGERED: Object.freeze({
    alarm: AlarmLevel.GELB,
    icon: "box-arrow-in-right",
  }),
  INFESTED: Object.freeze({
    alarm: AlarmLevel.ROT,
    icon: "exclamation-triangle",
  }),
  NULL: Object.freeze({
    alarm: AlarmLevel.GRUEN,
    icon: "check2-circle",
  }),
  REARMED: Object.freeze({
    alarm: AlarmLevel.GRUEN,
    icon: "check2-circle",
  }),
  LIGHT_INFESTATION: Object.freeze({
    alarm: AlarmLevel.GELB,
    icon: "exclamation-triangle",
  }),
  SERVERE_INFESTATION: Object.freeze({
    alarm: AlarmLevel.ROT,
    icon: "exclamation-triangle",
  }),
  FALSE_TRIGGERED: Object.freeze({
    alarm: AlarmLevel.GELB,
    icon: "box-arrow-in-right",
  }),
  ACTIVITY_WARNING: Object.freeze({
    alarm: AlarmLevel.GELB,
    icon: "box-arrow-in-right",
  }),
  ACTIVITY_CRITICAL: Object.freeze({
    alarm: AlarmLevel.GELB,
    icon: "exclamation-triangle",
  }),
  CATCH_DETECTED: Object.freeze({
    alarm: AlarmLevel.GELB,
    icon: "exclamation-triangle",
  }),

  // --- Zustände aus Device Events ---
  TRAP_TRIGGERED: Object.freeze({
    alarm: AlarmLevel.GELB,
    icon: "box-arrow-in-right",
  }),
  // CATCH_DETECTED - bereits oben definiert
  // REARMED - bereits oben definiert
  OFFLINE: Object.freeze({
    alarm: AlarmLevel.GELB,
    icon: "exclamation-triangle",
  }),
  ONLINE: Object.freeze({
    alarm: AlarmLevel.GRUEN,
    icon: "check2-circle",
  }),
  CONFIGURED: Object.freeze({
    alarm: AlarmLevel.GRUEN,
    icon: "check2-circle",
  }),
  MAINTAINED: Object.freeze({
    alarm: AlarmLevel.GRUEN,
    icon: "check2-circle",
  }),
  BATTERY_LOW: Object.freeze({
    alarm: AlarmLevel.GELB,
    icon: "box-arrow-in-right",
  }),
  IMAGE_RECEIVED: Object.freeze({
    alarm: AlarmLevel.GRUEN,
    icon: "check2-circle",
  }),

  // --- Zustände aus Device Status ---
  INACTIVE: Object.freeze({
    alarm: AlarmLevel.GELB,
    icon: "exclamation-triangle",
  }),
  ACTIVE: Object.freeze({
    alarm: AlarmLevel.GRUEN,
    icon: "check2-circle",
  }),
  HEARTBEAT_FAILED: Object.freeze({
    alarm: AlarmLevel.GELB,
    icon: "heart",
  }),
  // TRIGGERED - bereits oben definiert (Wird nicht überschrieben)
  LOW_BATTERY: Object.freeze({
    alarm: AlarmLevel.GELB,
    icon: "battery",
  }),
  LOCATION_MODE: Object.freeze({
    alarm: AlarmLevel.GELB,
    icon: "map",
  }),
  UNSTABLE_WIFI: Object.freeze({
    alarm: AlarmLevel.GELB,
    icon: "wifi-off",
  }),
  // LIGHT_INFESTATION - bereits oben definiert
  // SEVERE_INFESTATION -> SERVERE_INFESTATION
  // FALSE_TRIGGERED - bereits oben definiert
  // ACTIVITY_WARNING - bereits oben definiert
  // ACTIVITY_CRITICAL - bereits oben definiert
  // CATCH_DETECTED - bereits oben definiert
  BAIT_LOW: Object.freeze({
    alarm: AlarmLevel.GELB,
    icon: "exclamation-triangle",
  }),
  ACTIVITY_CRITICAL_BAIT_LOW: Object.freeze({
    alarm: AlarmLevel.GELB,
    icon: "exclamation-triangle",
  }),
  ACTIVITY_NOTICED: Object.freeze({
    alarm: AlarmLevel.GELB,
    icon: "box-arrow-in-right",
  }),
  ACTIVITY_THRESHOLD_REACHED: Object.freeze({
    alarm: AlarmLevel.GELB,
    icon: "exclamation-triangle",
  }),
  ACTIVITY_THRESHOLD_REACHED_BAIT_LOW: Object.freeze({
    alarm: AlarmLevel.GELB,
    icon: "exclamation-triangle",
  }),
  ACTIVITY_THRESHOLD_RESET: Object.freeze({
    alarm: AlarmLevel.GRUEN,
    icon: "check2-circle",
  }),
});

/**
 *
 * Hilfsfunktion: Nimmt einen Zustands-String entgegen und gibt den zugehörigen DeviceState zurück.
 *
 * Verhalten:
 * - case-insensitive ("online" == "ONLINE")
 * - trimmt Whitespaces
 * - gibt undefined zurück, wenn kein passender Zustand gefunden wurde
 * - enthält Alias für bekannte Schreibweise(n)
 *
 * ### Example usage
 *
 * import { getDeviceState, AlarmLevel } from "./DeviceStates";
 *
 * const level1 = getAlarmLevel("online").alarm;        // "GRUEN"
 * const level2 = getAlarmLevel("BATTERY_LOW").alarm;   // "GELB"
 * const level3 = getAlarmLevel("SEVERE_INFESTATION").alarm; // "ROT" (via alias)
 * const level4 = getAlarmLevel("unknown").alarm;       // undefined
 *
 * if (level1 === AlarmLevel.GRUEN) {
 *   // handle green
 * }
 *
 * @param {string} stateName
 * @returns DeviceState
 *
 */
export function getDeviceState(stateName) {

  if (stateName == null) return undefined

  // Normalisierung: TRIM + UPPERCASE
  const key = String(stateName).trim().toUpperCase()

  // Direkter Treffer
  const state = DeviceStates[key]
  if (state) return state

  // Aliase/Schreibvarianten abfangen (z. B. SEVERE vs. SERVERE)
  const aliases = Object.freeze({
    SEVERE_INFESTATION: 'SERVERE_INFESTATION'
  })

  const alt = aliases[key]
  if (alt && DeviceStates[alt]) {
    return DeviceStates[alt]
  }

  return undefined

}
